import { Link, NavLink, Outlet, useMatch } from "react-router-dom";
import { ChevronRight, Settings, TableIcon } from "lucide-react";

import { BreadcrumbProvider, useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";

function AppHeaderBreadcrumbs() {
  const { segments } = useBreadcrumbs();

  if (segments.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="text-muted-foreground flex min-w-0 flex-1 items-center gap-1 text-sm"
    >
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
        {segments.map((seg, i) => {
          const isLast = i === segments.length - 1;
          return (
            <li
              key={`${i}-${seg.label}`}
              className="flex min-w-0 items-center gap-1 truncate"
            >
              {i > 0 && (
                <ChevronRight
                  aria-hidden
                  className="text-muted-foreground/50 size-3.5 shrink-0"
                />
              )}
              {!isLast && seg.to ? (
                <Link
                  to={seg.to}
                  className="hover:text-foreground decoration-muted-foreground/50 underline-offset-4 hover:underline"
                >
                  {seg.label}
                </Link>
              ) : (
                <span
                  className="text-foreground font-medium"
                  {...(isLast ? { "aria-current": "page" as const } : {})}
                >
                  {seg.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function AppLayout() {
  const tablesActive = !!useMatch({ path: "/tables", end: false });
  const settingsActive = !!useMatch({ path: "/settings", end: true });

  return (
    <BreadcrumbProvider>
      <SidebarProvider defaultOpen={false} className="flex min-h-0 min-w-0 w-full flex-1 flex-row">
        <Sidebar collapsible="icon">
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={tablesActive} tooltip="Tables">
                      <NavLink to="/tables">
                        <TableIcon />
                        <span>Tables</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={settingsActive} tooltip="Settings">
                  <NavLink to="/settings">
                    <Settings />
                    <span>Settings</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset className="min-w-0 flex-1 flex-col">
          <header className="bg-background sticky top-0 z-10 flex h-10 shrink-0 items-center gap-2 border-b px-3">
            <SidebarTrigger />
            <AppHeaderBreadcrumbs />
          </header>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </BreadcrumbProvider>
  );
}
